import { useState, useEffect, useCallback, useRef } from 'react';
import { useApplication } from '../contexts/ApplicationContext';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';
import { ScanBatch, ScanListResponse } from '../types/scan';
import { useScanBatchProgress } from './useScanBatchProgress';

const POLL_INTERVAL_MS = 3000;

const SCAN_TYPE_NAMES: Record<string, string> = {
  secrets_scan: 'Secrets Scan',
  semgrep_scan: 'Security Scan',
  deep_ai_scan: 'Deep AI Security Scan',
  sbom_scan: 'SBOM Scan',
};

export type ScanBannerState = 'running' | 'complete';

export interface RepositoryScanProgress {
  state: ScanBannerState | null;
  batchId: string | null;
  scanTypeLabel: string;
  percent: number;
  findingsCount: number;
  /** Live phase label for the active batch (empty unless a scan is running). */
  currentOperation: string;
  applicationId: string | null;
  dismiss: () => void;
}

interface CompletedRecord {
  batchId: string;
  scanType: string;
  findingsCount: number;
}

/**
 * Repository-wide scan progress for the shell banner.
 *
 * Scoped to the currently selected repository (via ApplicationContext). Polls the
 * scans list for that repository to find the active batch, reuses
 * {@link useScanBatchProgress} for its live percentage, and surfaces a one-shot
 * "complete" state (with the batch's actionable findings count) once a scan that was
 * running finishes — until the user dismisses it. Returns `state: null` when there is
 * no repository in context or no relevant scan.
 */
export const useRepositoryScanProgress = (): RepositoryScanProgress => {
  const { applicationId } = useApplication();
  const api = useAuthenticatedApi();

  const [activeBatch, setActiveBatch] = useState<ScanBatch | null>(null);
  const [completed, setCompleted] = useState<CompletedRecord | null>(null);
  const [dismissedBatchId, setDismissedBatchId] = useState<string | null>(null);

  const lastActiveRef = useRef<ScanBatch | null>(null);
  const lastFindingsRef = useRef(0);
  const isMountedRef = useRef(true);

  const { percentComplete, findingsCount, currentOperation } = useScanBatchProgress(activeBatch?.id ?? null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Remember the latest findings count while a scan is running so the "complete"
  // banner can fall back to it before the completed batch shows up in the list.
  useEffect(() => {
    if (activeBatch) lastFindingsRef.current = findingsCount;
  }, [activeBatch, findingsCount]);

  // Reset everything when switching repositories.
  useEffect(() => {
    setActiveBatch(null);
    setCompleted(null);
    setDismissedBatchId(null);
    lastActiveRef.current = null;
    lastFindingsRef.current = 0;
  }, [applicationId]);

  const poll = useCallback(async () => {
    if (!applicationId) return;
    try {
      const url = `${apiEndpoints.scans.list()}?application_id=${applicationId}&limit=20`;
      const response = await api.get<ScanListResponse>(url);
      if (!isMountedRef.current) return;

      const queued = response.queued_batches || [];
      const nextActive = queued.length
        ? [...queued].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          )[0]
        : null;

      if (nextActive) {
        setActiveBatch(nextActive);
        lastActiveRef.current = nextActive;
        // A fresh scan supersedes any lingering "complete" banner.
        setCompleted(null);
      } else {
        const prev = lastActiveRef.current;
        if (prev) {
          const finishedBatch = (response.batches || []).find((b) => b.id === prev.id);
          setCompleted({
            batchId: prev.id,
            scanType: prev.scan_type,
            findingsCount: finishedBatch?.actionable_findings_count ?? lastFindingsRef.current,
          });
          lastActiveRef.current = null;
        }
        setActiveBatch(null);
      }
    } catch (err) {
      console.error('Error polling repository scan progress:', err);
    }
  }, [api, applicationId]);

  useEffect(() => {
    if (!applicationId) return;
    poll();
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [applicationId, poll]);

  const dismiss = useCallback(() => {
    setCompleted((current) => {
      if (current) setDismissedBatchId(current.batchId);
      return current;
    });
  }, []);

  let state: ScanBannerState | null = null;
  let batchId: string | null = null;
  let scanType = '';
  let percent = 0;
  let findings = 0;

  if (activeBatch) {
    state = 'running';
    batchId = activeBatch.id;
    scanType = activeBatch.scan_type;
    percent = percentComplete;
    findings = findingsCount;
  } else if (completed && completed.batchId !== dismissedBatchId) {
    state = 'complete';
    batchId = completed.batchId;
    scanType = completed.scanType;
    percent = 100;
    findings = completed.findingsCount;
  }

  return {
    state,
    batchId,
    scanTypeLabel: SCAN_TYPE_NAMES[scanType] || 'Security Scan',
    percent,
    findingsCount: findings,
    currentOperation: state === 'running' ? currentOperation : '',
    applicationId,
    dismiss,
  };
};

export default useRepositoryScanProgress;
